import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronDown, Flame, Droplet, Shield, Calendar } from 'lucide-react';
import { usePortfolioScopeParams } from '@/lib/portfolioScopeParams';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PersonalStreaksCard — Round 84k (E).
 *
 * Gamified streak panel that recognises the user's ongoing dividend
 * discipline. Powered by GET /api/portfolio/streaks. Pure math over
 * data already in the DB (positions, stock_cache.dividend_cut_observed_at,
 * portfolio_dividend_events) so no external API calls.
 */
function StreakTile({ icon: Icon, label, value, suffix, tone = 'violet', testid, caption }) {
  const toneClasses = {
    violet: 'text-violet-700 dark:text-violet-300 border-violet-300/40 dark:border-violet-700/40 bg-violet-50/60 dark:bg-violet-950/20',
    emerald: 'text-emerald-700 dark:text-emerald-300 border-emerald-300/40 dark:border-emerald-700/40 bg-emerald-50/60 dark:bg-emerald-950/20',
    amber: 'text-amber-700 dark:text-amber-300 border-amber-300/40 dark:border-amber-700/40 bg-amber-50/60 dark:bg-amber-950/20',
    sky: 'text-sky-700 dark:text-sky-300 border-sky-300/40 dark:border-sky-700/40 bg-sky-50/60 dark:bg-sky-950/20',
  }[tone];

  return (
    <div className={`rounded-lg border p-3 ${toneClasses}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold opacity-80">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-2xl font-bold font-mono">{value}</span>
        {suffix && <span className="text-[10px] opacity-80">{suffix}</span>}
      </div>
      {/* Round P112o — Optional context caption under the value so
          users know WHICH ticker(s) drive each tile. */}
      {caption && (
        <div className="text-[10px] opacity-75 mt-0.5 truncate" title={caption}>
          {caption}
        </div>
      )}
    </div>
  );
}

/**
 * BestDripStreakTile — Round 92t+. Replaces the original
 * "Consecutive DRIPs" tile. The headline still shows a streak count,
 * but it's the BEST PER-TICKER run instead of the strict global one
 * — much more motivating on a 20-stock portfolio where one cash sweep
 * on any holding would otherwise wipe the global counter.
 *
 * The whole tile is a dropdown: collapsed shows the top ticker's
 * streak; the chevron at bottom-right ("All Tickers") expands the
 * full list of every ticker with an active DRIP run, top 3 visible
 * at the natural panel height and the rest reachable via native
 * scroll (mouse wheel on desktop, swipe on touch).
 *
 * `globalConsecutive` is shown as a small footnote inside the popover
 * so portfolio-wide discipline still has a visible home.
 */
function BestDripStreakTile({ rows, globalConsecutive, bestEver = 0, bestEverTicker = null }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Outside-click closer so the popover doesn't trap the user.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const best = rows[0];
  // Round P112o — Fall back to all-time BEST when no ticker has a
  // current active run (a break just happened, but we still want to
  // celebrate the historical peak). Card headline follows the best
  // ever + its responsible ticker.
  const displayStreak = best ? best.consecutive_drips : bestEver;
  const displayTicker = best ? best.ticker : bestEverTicker;
  const isHistorical = !best && bestEver > 0;
  // Round 92t+++ — Milestone tone tiers. The tile's palette quietly
  // upgrades as the leader streak grows so the user gets a visible
  // "level-up" reward for sustained discipline. Thresholds match the
  // natural mental milestones (a year of monthly DRIPs ≈ 12, several
  // years of quarterly DRIPs ≈ 20). The "no leader" amber stays as
  // the call-to-action state since amber reads "warning / start here".
  const bestStreak = displayStreak;
  let tone;
  if (!best || bestStreak < 1) {
    tone = 'text-amber-700 dark:text-amber-300 border-amber-300/40 dark:border-amber-700/40 bg-amber-50/60 dark:bg-amber-950/20';
  } else if (bestStreak < 5) {
    tone = 'text-violet-700 dark:text-violet-300 border-violet-300/40 dark:border-violet-700/40 bg-violet-50/60 dark:bg-violet-950/20';
  } else if (bestStreak < 10) {
    tone = 'text-sky-700 dark:text-sky-300 border-sky-300/40 dark:border-sky-700/40 bg-sky-50/60 dark:bg-sky-950/20';
  } else if (bestStreak < 20) {
    tone = 'text-emerald-700 dark:text-emerald-300 border-emerald-300/40 dark:border-emerald-700/40 bg-emerald-50/60 dark:bg-emerald-950/20';
  } else {
    // Gold tier — actual `amber-` 600+ shades on a soft-amber wash so
    // 20+ feels distinct from both the "empty" amber and the lower
    // tiers. Subtle glow via ring + shadow so it pops without yelling.
    tone = 'text-amber-800 dark:text-amber-200 border-amber-400/60 dark:border-amber-600/60 bg-gradient-to-br from-amber-50/80 to-yellow-100/60 dark:from-amber-950/40 dark:to-yellow-900/30 ring-1 ring-amber-400/30 dark:ring-amber-500/20 shadow-sm';
  }

  return (
    <div className={`relative rounded-lg border p-3 ${tone}`} ref={wrapRef} data-testid="streak-best-drip">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold opacity-80">
        <Flame className="w-3 h-3" />
        Best DRIP Streak
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-2xl font-bold font-mono" data-testid="best-drip-value">
          {displayStreak}
        </span>
        {displayTicker && displayStreak > 0 ? (
          <span className="text-[10px] opacity-80 flex items-baseline gap-0.5">
            {isHistorical ? 'best · ' : 'on'}
            <Flame className="w-3 h-3 text-orange-500 shrink-0 self-center" aria-hidden="true" />
            <span className="font-mono font-semibold text-[13px]" data-testid="best-drip-ticker">{displayTicker}</span>
          </span>
        ) : (
          <span className="text-[10px] opacity-80">in a row</span>
        )}
      </div>
      {/* Bottom-right chevron toggle. Only render when there's at
          least one ticker with an active streak — nothing to expand
          otherwise. The tooltip ("All Tickers") doubles as the user's
          mental label for the action. */}
      {rows.length > 0 && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="absolute bottom-1.5 right-1.5 flex items-center gap-0.5 text-[9px] uppercase font-bold tracking-wide opacity-70 hover:opacity-100 transition-opacity"
          title="View streaks across all your tickers"
          data-testid="best-drip-toggle"
          aria-expanded={open}
          aria-controls="best-drip-list"
        >
          All Tickers
          <ChevronDown
            className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
      )}
      {/* Pop-down list. Positioned absolutely so it overlays the row
          below without shifting layout. `max-h-44` shows ~3 rows at
          natural row height (~h-12 each); native `overflow-y-auto`
          handles wheel + swipe scrolling identically on desktop /
          tablet / phone. */}
      {open && (
        <div
          id="best-drip-list"
          className="absolute left-0 right-0 top-full mt-1 z-30 rounded-lg border border-border bg-popover shadow-lg overflow-hidden"
          data-testid="best-drip-list"
        >
          <div className="max-h-44 overflow-y-auto overscroll-contain">
            <ul className="divide-y divide-border/60">
              {rows.map((r, i) => (
                <li
                  key={r.ticker}
                  className="flex items-baseline justify-between px-3 py-2 text-xs"
                  data-testid={`best-drip-row-${r.ticker}`}
                >
                  <span className="flex items-baseline gap-1.5 min-w-0">
                    <span className="text-[10px] text-muted-foreground font-mono w-4 shrink-0">{i + 1}.</span>
                    <span className="font-mono font-semibold text-foreground truncate">{r.ticker}</span>
                  </span>
                  <span className="font-mono shrink-0">
                    <span className="font-bold text-foreground">{r.consecutive_drips}</span>
                    <span className="text-muted-foreground"> in a row</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="px-3 py-1.5 text-[10px] text-muted-foreground border-t border-border/60 bg-muted/40 font-mono">
            Portfolio-wide: <span className="font-semibold text-foreground">{globalConsecutive}</span> in a row across every ticker
          </div>
        </div>
      )}
    </div>
  );
}

export default function PersonalStreaksCard({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const scopeParams = usePortfolioScopeParams();
  const scopeKey = scopeParams.portfolio_id || 'all';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/portfolio/streaks`, { params: scopeParams, withCredentials: true })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey, scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !data) return null;
  // Don't render for empty portfolios — nothing motivating to show.
  if (!data.active_tickers || data.active_tickers === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-4 mb-4" data-testid="personal-streaks-card">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-500" />
          <h3 className="text-sm font-bold">Your Dividend Streaks</h3>
        </div>
        {/* Round 95bp (Jun 2026) — DRIP Calculator launcher moved to the
            Monthly Dividend Goal card per user request. */}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StreakTile
          icon={Shield}
          label="Cuts-free streak"
          value={data.cuts_free_months}
          suffix="months"
          tone={data.cuts_free_months > 0 ? 'emerald' : 'amber'}
          testid="streak-cuts-free"
          caption={
            data.cuts_free_months === 0 && (data.cut_tickers || []).length > 0
              ? `cut flagged on ${data.cut_tickers.join(', ')}`
              : data.active_tickers > 0
              ? `across ${data.active_tickers} holding${data.active_tickers === 1 ? '' : 's'}`
              : null
          }
        />
        <StreakTile
          icon={Droplet}
          label="DRIP %"
          value={data.drip_percentage}
          suffix="%"
          tone={data.drip_percentage >= 50 ? 'sky' : 'violet'}
          testid="streak-drip-pct"
          caption={
            data.drip_total_actions > 0
              ? `${data.drip_total_actions} dividend event${data.drip_total_actions === 1 ? '' : 's'}`
              : 'no reinvestments yet'
          }
        />
        <BestDripStreakTile
          rows={data.per_ticker_drip_streaks || []}
          globalConsecutive={data.consecutive_drips || 0}
          bestEver={data.best_drip_streak_ever || 0}
          bestEverTicker={data.best_drip_streak_ticker || null}
        />
        <StreakTile
          icon={Calendar}
          label="Portfolio age"
          value={data.portfolio_age_months}
          suffix="months"
          tone="violet"
          testid="streak-portfolio-age"
          caption={
            data.earliest_holding_at
              ? `since ${new Date(data.earliest_holding_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}${data.earliest_holding_ticker ? ` · ${data.earliest_holding_ticker}` : ''}`
              : null
          }
        />
      </div>
      {data.drip_total_actions === 0 && (
        <p className="text-xs text-foreground/80 mt-3 leading-relaxed font-medium" data-testid="streak-drip-help">
          <Droplet className="inline-block w-3.5 h-3.5 mr-1 -mt-0.5 text-sky-500" />
          <span className="font-bold">DRIP % populates once you reinvest a dividend.</span>{' '}
          Either click the <span className="font-bold">water-drop icon</span> next to any buy in your portfolio, or use <span className="font-bold">&ldquo;Reinvest&rdquo;</span> on a paid dividend reminder.
        </p>
      )}
    </div>
  );
}
